import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BookOpen, Send, X, MessageCircle, History, Award, 
  Loader2, BookMarked, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookChat() {
  const { getAuthHeaders } = useAuth();
  const [activeChat, setActiveChat] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [bookTitle, setBookTitle] = useState('');
  const [bookAuthor, setBookAuthor] = useState('');
  const [starting, setStarting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [activeChat?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchData = async () => {
    try {
      const [activeRes, historyRes] = await Promise.all([
        axios.get(`${API}/student/book-chat/active`, { headers: getAuthHeaders() }),
        axios.get(`${API}/student/book-chat/history`, { headers: getAuthHeaders() }),
      ]);
      setActiveChat(activeRes.data);
      setChatHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching book chat data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startChat = async () => {
    if (!bookTitle.trim()) {
      toast.error('Кітап атауын енгізіңіз');
      return;
    }
    
    setStarting(true);
    try {
      const response = await axios.post(`${API}/student/book-chat/start`, {
        book_title: bookTitle.trim(),
        book_author: bookAuthor.trim() || null
      }, { headers: getAuthHeaders() });
      
      setActiveChat({
        id: response.data.chat_id,
        book_title: bookTitle,
        book_author: bookAuthor,
        messages: [{ role: 'ai', content: response.data.message }],
        status: 'active'
      });
      setBookTitle('');
      setBookAuthor('');
      setDialogOpen(false);
      toast.success('Сөйлесу басталды!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !activeChat) return;
    
    const userMessage = message.trim();
    setMessage('');
    setSending(true);
    
    // Optimistically add user message
    setActiveChat(prev => ({
      ...prev,
      messages: [...prev.messages, { role: 'student', content: userMessage }]
    }));
    
    try {
      const response = await axios.post(
        `${API}/student/book-chat/${activeChat.id}/message`,
        { message: userMessage },
        { headers: getAuthHeaders() }
      );
      
      // Add AI response
      setActiveChat(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'ai', content: response.data.message }],
        status: response.data.status
      }));
      
      if (response.data.status === 'completed') {
        toast.success(`🎉 Сөйлесу аяқталды! +${response.data.merit_earned} merit ұпай казандыңыз!`);
        fetchData(); // Refresh history
      }
    } catch (error) {
      toast.error('Хабарлама жіберу кезінде қате орын алды');
      // Remove optimistic message on error
      setActiveChat(prev => ({
        ...prev,
        messages: prev.messages.slice(0, -1)
      }));
    } finally {
      setSending(false);
    }
  };

  const cancelChat = async () => {
    if (!activeChat) return;
    
    try {
      await axios.delete(`${API}/student/book-chat/${activeChat.id}`, {
        headers: getAuthHeaders()
      });
      setActiveChat(null);
      toast.info('Сөйлесу жойылды');
    } catch (error) {
      toast.error('Қате орын алды');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <Card className="paper-card">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="paper-card border-l-4 border-l-blue-500">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-500" />
          Кітап сөйлесуі
        </CardTitle>
        <p className="text-xs text-muted">Кітап оқып, AI-мен талқылаңыз және merit ұпай жинаңыз!</p>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={activeChat ? "chat" : "start"} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="start" disabled={!!activeChat}>
              <BookMarked className="w-4 h-4 mr-2" />
              Жаңа
            </TabsTrigger>
            <TabsTrigger value="chat" disabled={!activeChat}>
              <MessageCircle className="w-4 h-4 mr-2" />
              Сөйлесу
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="w-4 h-4 mr-2" />
              Тарих
            </TabsTrigger>
          </TabsList>

          {/* Start New Chat Tab */}
          <TabsContent value="start">
            {activeChat ? (
              <div className="text-center py-6 text-muted">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                <p>Сізде белсенді сөйлесу бар</p>
                <p className="text-sm">"{activeChat.book_title}" кітабы бойынша</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 text-blue-500/50" />
                  <h3 className="font-semibold text-lg mb-2">Кітап оқып бітірдіңіз бе?</h3>
                  <p className="text-sm text-muted mb-4">
                    AI кітапханашымен сөйлесіп, кітапты талқылаңыз және merit ұпай жинаңыз!
                  </p>
                </div>
                
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Сөйлесуді бастау
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Жаңа кітап сөйлесуі</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="book_title">Кітап атауы *</Label>
                        <Input
                          id="book_title"
                          value={bookTitle}
                          onChange={(e) => setBookTitle(e.target.value)}
                          placeholder="Мысалы: Абай жолы"
                        />
                      </div>
                      <div>
                        <Label htmlFor="book_author">Автор (міндетті емес)</Label>
                        <Input
                          id="book_author"
                          value={bookAuthor}
                          onChange={(e) => setBookAuthor(e.target.value)}
                          placeholder="Мысалы: Мұхтар Әуезов"
                        />
                      </div>
                      <Button 
                        onClick={startChat} 
                        disabled={starting || !bookTitle.trim()}
                        className="w-full"
                      >
                        {starting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Басталуда...
                          </>
                        ) : (
                          <>
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Сөйлесуді бастау
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">💡 Қалай жұмыс істейді?</h4>
                  <ol className="text-sm text-slate-700 dark:text-slate-300 space-y-1.5">
                    <li>1. Оқыған кітабыңыздың атауын енгізіңіз</li>
                    <li>2. AI кітапханашы сізге 5 сұрақ қояды</li>
                    <li>3. Жауаптарыңызға байланысты merit ұпай аласыз</li>
                    <li>4. Максимум 10 ұпай жинай аласыз!</li>
                  </ol>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Active Chat Tab */}
          <TabsContent value="chat">
            {activeChat ? (
              <div className="space-y-4">
                {/* Chat Header */}
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800 rounded-lg border border-blue-100 dark:border-slate-700">
                  <div>
                    <p className="font-semibold text-slate-800 dark:text-slate-100">{activeChat.book_title}</p>
                    {activeChat.book_author && (
                      <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">{activeChat.book_author}</p>
                    )}
                  </div>
                  {activeChat.status === 'active' && (
                    <Button variant="ghost" size="sm" onClick={cancelChat}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Messages */}
                <div className="h-[300px] overflow-y-auto space-y-3 p-3 border rounded-lg bg-slate-50 dark:bg-slate-900">
                  {activeChat.messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'student' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                          msg.role === 'student'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-start">
                      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-3 shadow-sm">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                {activeChat.status === 'active' ? (
                  <div className="flex gap-2">
                    <Input
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Жауабыңызды жазыңыз..."
                      disabled={sending}
                    />
                    <Button onClick={sendMessage} disabled={sending || !message.trim()}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <Award className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    <p className="font-semibold text-green-700 dark:text-green-300">
                      Сөйлесу аяқталды!
                    </p>
                    <p className="text-sm text-green-600">
                      +{activeChat.merit_earned || 0} merit ұпай қосылды
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => { setActiveChat(null); fetchData(); }}
                    >
                      Жаңа сөйлесу бастау
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Белсенді сөйлесу жоқ</p>
                <p className="text-sm">Жаңа сөйлесу бастау үшін "Жаңа" қойындысына өтіңіз</p>
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            {chatHistory.length === 0 ? (
              <div className="text-center py-8 text-muted">
                <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Әзірге сөйлесу тарихы жоқ</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[350px] overflow-y-auto">
                {chatHistory.map((chat) => (
                  <Dialog key={chat.id}>
                    <DialogTrigger asChild>
                      <div
                        className="p-3 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
                        onClick={() => setSelectedHistory(chat)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-800 dark:text-slate-100">{chat.book_title}</p>
                            {chat.book_author && (
                              <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">{chat.book_author}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-green-600">
                              <Star className="w-4 h-4 fill-green-600" />
                              <span className="font-bold">+{chat.merit_earned}</span>
                            </div>
                            <p className="text-xs text-muted">
                              {format(new Date(chat.created_at), 'dd.MM.yyyy')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{chat.book_title}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 bg-slate-50 dark:bg-slate-900 p-3 rounded-lg">
                        {chat.messages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`flex ${msg.role === 'student' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                                msg.role === 'student'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                            </div>
                          </div>
                        ))}
                        <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <p className="font-semibold text-green-700">
                            Нәтиже: +{chat.merit_earned} merit ұпай
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
